import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Camera, X, ChevronLeft, ChevronRight, Instagram } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ELFSIGHT_APP_ID = process.env.REACT_APP_ELFSIGHT_APP_ID;

const PhotoUpdates = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  // Fetch photos from API
  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/photo-updates`);
        setPhotos(response.data);
      } catch (error) {
        console.error('Error fetching photos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPhotos();
  }, []);

  const openLightbox = (index) => {
    setCurrentPhotoIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
  };

  const prevPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Lightbox Modal */}
      {lightboxOpen && photos.length > 0 && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={closeLightbox}>
          <button 
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-50"
          >
            <X className="h-8 w-8" />
          </button>
          
          {photos.length > 1 && (
            <button 
              onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
              className="absolute left-4 text-white hover:text-gray-300 z-50"
            >
              <ChevronLeft className="h-12 w-12" />
            </button>
          )}
          
          <img
            src={photos[currentPhotoIndex]?.photoUrl}
            alt={photos[currentPhotoIndex]?.caption || 'Photo'}
            className="max-h-[85vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          
          {photos.length > 1 && (
            <button 
              onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
              className="absolute right-4 text-white hover:text-gray-300 z-50"
            >
              <ChevronRight className="h-12 w-12" />
            </button>
          )}
          
          <div className="absolute bottom-4 text-white text-center">
            <p className="text-sm">{photos[currentPhotoIndex]?.dogName}</p>
            <p className="text-xs text-gray-400 mt-1">{currentPhotoIndex + 1} of {photos.length}</p>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary/10 via-background to-secondary/30 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              Furry Friends Photos
            </h1>
            <p className="text-xl text-muted-foreground">
              A glimpse into the happy moments at Sadie's Mission Dog Boarding.
            </p>
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <section className="section-padding bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : photos.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {photos.map((photo, index) => (
                  <div 
                    key={photo.id}
                    onClick={() => openLightbox(index)}
                    className="aspect-square rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all cursor-pointer hover:scale-105"
                  >
                    <img
                      src={photo.photoUrl}
                      alt={photo.caption || photo.dogName}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <Camera className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-foreground mb-2">No Photos Yet</h3>
                <p className="text-muted-foreground">
                  Check back soon for photos of our furry guests!
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Instagram Feed Section */}
      <section className="section-padding bg-secondary/30" data-testid="instagram-feed-section">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Instagram className="h-7 w-7 text-primary" />
              <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                Follow Us on Instagram
              </h2>
            </div>
            <p className="text-center text-muted-foreground mb-8">
              Latest posts from <a href="https://www.instagram.com/sadiesmission_bc/" target="_blank" rel="noopener noreferrer" className="text-primary font-semibold hover:underline">@sadiesmission_bc</a>
            </p>

            {/* Elfsight Instagram Widget */}
            {ELFSIGHT_APP_ID ? (
              <div className={`elfsight-app-${ELFSIGHT_APP_ID}`} data-elfsight-app-lazy data-testid="instagram-widget"></div>
            ) : (
              <div className="text-center py-8">
                <a
                  href="https://www.instagram.com/sadiesmission_bc/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-full font-medium hover:bg-primary/90 transition-colors"
                  data-testid="instagram-follow-btn"
                >
                  <Instagram className="h-5 w-5" />
                  Follow @sadiesmission_bc
                </a>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PhotoUpdates;
